import type { EditorCaseData } from '@messenger/lotus'
import {
  EDITOR_FIELD_CATALOG,
  EDITOR_FIELD_NAME_OF_DECEASED,
  EDITOR_NO_BREAK_SPACE,
  editorNameArrangementCaseData,
  editorServiceColumnKey,
  editorServiceIndexKey,
  editorTableCellCountKey,
  editorTableCellKey,
  editorTableRowTypeIndexKey,
} from '@messenger/lotus'
import {
  formatCityStatePostal,
  isXamlRichText,
  orderedBySequence,
  xamlParagraphsAsHtml,
  xamlRichTextToHtmlIfLossless,
} from '@messenger/utils'
import { nameArrangementSchema } from '@messenger/types'
import type { Locale } from '@messenger/types'
import { M3 } from '@messenger/consts'
import type { Case } from 'types/case'
import calculateCaseAge from 'utils/sanitizers/calculateCaseAge'
import isoStringToDate from 'utils/dates/isoStringToDate'
import {
  FIELD_SOURCES,
  FH_FIELD_SOURCES,
  serviceBlockPartKey,
  serviceDefaultIdKey,
  trimWrittenCaseValue,
} from './fieldRules'
import {
  additionalFamilyRowType,
  additionalFamilyRowTypes,
  isHonoraryPallbearerRow,
} from './tabularCaseRows'

export interface EditorTextBoxCaseContext {
  dateFormat: string
  locale: Locale
  formatDate: (
    date: Date | string,
    format: string,
    isMomentJSFormat: boolean,
    locale: Locale,
  ) => string
  funeralHomeDetails?: Record<string, unknown> | null
  themeApplication?: string
  translate?: (key: string, values?: Record<string, string>) => string
}

export interface FieldRule {
  key: string
  from: string
  format?: (
    raw: unknown,
    ctx?: EditorTextBoxCaseContext,
    caseDetails?: Record<string, unknown>,
  ) => string
}

const asDate = (raw: unknown, ctx?: EditorTextBoxCaseContext): string =>
  raw == null || !ctx
    ? ''
    : ctx.formatDate(raw as Date | string, ctx.dateFormat, false, ctx.locale)

const asYear = (raw: unknown): string => {
  if (raw == null || raw === '') return ''
  const normalized = isoStringToDate(raw as string | Date)
  if (!(normalized instanceof Date)) return ''
  const year = normalized.getFullYear()
  return Number.isFinite(year) ? String(year) : ''
}

const asFirstLetter = (raw: unknown): string =>
  typeof raw === 'string' && raw.trim() !== ''
    ? raw.trim().charAt(0).toUpperCase()
    : ''

const derivedAgeCache = new WeakMap<object, Record<string, unknown>>()
const caseAgeTriple = (
  caseDetails: Record<string, unknown>,
): Record<string, unknown> => {
  const cached = derivedAgeCache.get(caseDetails)
  if (cached) return cached
  const scratch = {
    date_of_birth: caseDetails.date_of_birth,
    date_of_death: caseDetails.date_of_death,
  } as unknown as Case
  calculateCaseAge(scratch)
  const triple = scratch as unknown as Record<string, unknown>
  derivedAgeCache.set(caseDetails, triple)
  return triple
}

const asAge =
  (key: 'age_years' | 'age_months' | 'age_days') =>
  (
    raw: unknown,
    _ctx?: EditorTextBoxCaseContext,
    caseDetails?: Record<string, unknown>,
  ): string => {
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw)
    const derived = caseAgeTriple(caseDetails ?? {})[key]
    return typeof derived === 'number' && Number.isFinite(derived)
      ? String(derived)
      : ''
  }

const BLOCK_TAG_NAMES = 'p|div|li|h[1-6]|blockquote'

const HTML_BLOCK_BOUNDARY = new RegExp(
  `(?:</(?:${BLOCK_TAG_NAMES})\\s*>|<br\\s*/?>)[ \\t\\n]*`,
  'gi',
)
const AFTER_BLOCK_OPEN_SOURCE_WS = new RegExp(
  `(<(?:${BLOCK_TAG_NAMES})\\b[^<>]*>)[ \\t]*\\n[ \\t\\n]*`,
  'gi',
)
// Each whitespace run is matched once, with the block close that may end it,
// so a long run that no close ends costs one scan; the run goes only when it
// holds a line break and a block close ends it.
const SOURCE_WS_BEFORE_BLOCK_CLOSE = new RegExp(
  `[ \\t\\n]+(</(?:${BLOCK_TAG_NAMES})\\s*>)?`,
  'gi',
)
const dropSourceWsBeforeBlockClose = (
  run: string,
  close: string | undefined,
): string =>
  close !== undefined && run.slice(0, run.length - close.length).includes('\n')
    ? close
    : run

const stripTagsLinear = (input: string): string => {
  let out = ''
  let insideTag = false
  for (const ch of input) {
    if (ch === '<') insideTag = true
    else if (ch === '>') insideTag = false
    else if (!insideTag) out += ch
  }
  return out
}

const fromCodePointSafe = (cp: number): string =>
  !Number.isInteger(cp) ||
  cp < 0 ||
  cp > 0x10ffff ||
  (cp >= 0xd800 && cp <= 0xdfff)
    ? ''
    : String.fromCodePoint(cp)

const decodeHtmlEntities = (input: string): string =>
  input
    .replace(/&#x([0-9a-fA-F]+);/g, (_m, hex: string) =>
      fromCodePointSafe(parseInt(hex, 16)),
    )
    .replace(/&#(\d+);/g, (_m, dec: string) =>
      fromCodePointSafe(parseInt(dec, 10)),
    )
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&nbsp;/g, EDITOR_NO_BREAK_SPACE)
    .replace(/&amp;/g, '&')

const collapseBlankLineRuns = (lines: readonly string[]): string[] => {
  const kept: string[] = []
  for (const line of lines)
    if (line !== '' || kept[kept.length - 1] !== '') kept.push(line)
  return kept
}

// `[^<>]*` stops at the next tag's `<`, so a tag missing its `>` costs one
// scan to that `<` and the whole pass stays linear.
const TABLE_TAG = /<(\/?)(table|caption|tr|t[dh])\b[^<>]*>/gi
// Each tag of a table inside a cell parts that cell's words with a space.
const NESTED_TABLE_TAG = /<\/?(?:table|caption|tr|t[dh])\b[^<>]*>/gi
const NO_BREAK_SPACE = /&nbsp;|&#0*160;|&#x0*a0;|\u00a0/gi
const ENDS_IN_NO_BREAK_SPACE = /(?:&nbsp;|&#0*160;|&#x0*a0;|\u00a0)$/i
const BLOCK_CLOSE_AHEAD = new RegExp(
  `([ \\t\\n]*)</(?:${BLOCK_TAG_NAMES})\\s*>`,
  'iy',
)

/**
 * One cell's words on one line: its own paragraphs and breaks become spaces.
 * A no-break space stays one, so the words it holds together stay together,
 * and a cell holding nothing else prints nothing.
 */
const tableCellText = (cell: string): string => {
  const text = stripTagsLinear(
    cell.replace(HTML_BLOCK_BOUNDARY, ' ').replace(NESTED_TABLE_TAG, ' '),
  ).replace(/[ \t\n\r]+/g, ' ')
  if (text.replace(NO_BREAK_SPACE, '').trim() === '') return ''
  const start = text.startsWith(' ') ? 1 : 0
  const end = text.endsWith(' ') ? text.length - 1 : text.length
  return text.slice(start, end)
}

/**
 * A row's cells joined by a space, unless the cell before already ends in a
 * no-break space. Each cell is tested once, never the row built so far, so a
 * wide row costs one pass.
 */
const rowText = (cells: readonly string[]): string =>
  cells.reduce((row, cell, i) =>
    ENDS_IN_NO_BREAK_SPACE.test(cells[i - 1])
      ? `${row}${cell}`
      : `${row} ${cell}`,
  )

/** Whether the words before a table end their line; tags alone end none. */
const endsLine = (html: string): boolean => {
  const text = stripTagsLinear(html)
  let at = text.length - 1
  while (at >= 0 && (text[at] === ' ' || text[at] === '\t')) at -= 1
  return at < 0 || text[at] === '\n'
}

/**
 * Where a block close that follows `after` starts, past the source whitespace
 * before it, or -1 when no block close follows.
 */
const blockCloseAfter = (html: string, after: number): number => {
  BLOCK_CLOSE_AHEAD.lastIndex = after
  const match = BLOCK_CLOSE_AHEAD.exec(html)
  return match ? after + match[1].length : -1
}

/**
 * The rows as paragraphs. When a block closes right after the table, the last
 * row leaves its paragraph open for that close to end, so the row does not
 * end its line twice.
 */
const rowParagraphs = (rows: readonly string[], closeLast: boolean): string =>
  rows
    .map((row, i) =>
      i === rows.length - 1 && !closeLast ? `<p>${row}` : `<p>${row}</p>`,
    )
    .join('')

/**
 * Each table becomes one paragraph per row, the row's non-empty cells joined
 * as `rowText` joins them, so a row prints as one line however its cells are
 * built; a caption is a line of its own. A table inside a cell joins that cell's
 * words. A table never closed keeps the rows it closed, and the HTML after
 * its last closed row reads as ordinary text, up to the next table outside a
 * cell, which starts a table of its own.
 */
const flattenTables = (html: string): string => {
  let out = ''
  let from = 0
  let depth = 0
  let rows: string[] = []
  let cells: string[] = []
  let cellStart = -1
  let rowsClosed = 0
  let afterLastRow = -1
  const closeCell = (at: number) => {
    if (cellStart < 0) return
    const text = tableCellText(html.slice(cellStart, at))
    if (text) cells.push(text)
    cellStart = -1
  }
  const closeRow = () => {
    if (cells.length > 0) rows.push(rowText(cells))
    cells = []
  }
  // The rows end where the table does; when a block close follows, the source
  // whitespace before it goes with the table, so no row line ends in spaces.
  const endTable = (closedRows: readonly string[], after: number) => {
    const close = blockCloseAfter(html, after)
    out += rowParagraphs(closedRows, close < 0)
    from = close < 0 ? after : close
  }
  const endUnclosedTable = () =>
    endTable(rows.slice(0, rowsClosed), afterLastRow)
  for (const tag of html.matchAll(TABLE_TAG)) {
    const closing = tag[1] === '/'
    const name = tag[2].toLowerCase()
    const at = tag.index
    const end = at + tag[0].length
    if (name === 'table' && !closing) {
      if (depth > 0 && cellStart >= 0) {
        depth += 1
        continue
      }
      if (depth > 0) endUnclosedTable()
      depth = 1
      // The rows start a line of their own: words right against the table get
      // a newline, and a block boundary before it collapses the one it adds.
      const before = html.slice(from, at)
      out += endsLine(before) ? before : `${before}\n`
      rows = []
      cells = []
      cellStart = -1
      rowsClosed = 0
      afterLastRow = end
      continue
    }
    if (depth === 0) continue
    if (name === 'table') {
      depth -= 1
      if (depth > 0) continue
      closeCell(at)
      closeRow()
      endTable(rows, end)
      continue
    }
    if (depth > 1) continue
    if (name === 'td' || name === 'th' || name === 'caption') {
      closeCell(at)
      if (name === 'caption') closeRow()
      if (!closing) cellStart = end
      continue
    }
    closeCell(at)
    closeRow()
    if (closing) {
      rowsClosed = rows.length
      afterLastRow = end
    }
  }
  if (depth > 0) endUnclosedTable()
  return out + html.slice(from)
}

export const obituaryToPlainText = (raw: unknown): string => {
  if (typeof raw !== 'string') return ''
  // A XAML value that does not convert without loss (its visible text would
  // change, it holds an HTML paragraph, line break or block, or it is over
  // 100 000 characters) is read as HTML with each `<Paragraph>` made a `<p>` (a
  // `<div>` when it holds an HTML paragraph or other block element), as the
  // case rich-text step reads it, so its paragraphs keep their line breaks
  // here too.
  const html =
    xamlRichTextToHtmlIfLossless(raw) ??
    (isXamlRichText(raw) ? xamlParagraphsAsHtml(raw) : raw)
  if (!html) return ''
  const text = decodeHtmlEntities(
    stripTagsLinear(
      flattenTables(html.replace(/\r\n?/g, '\n'))
        .replace(AFTER_BLOCK_OPEN_SOURCE_WS, '$1')
        .replace(SOURCE_WS_BEFORE_BLOCK_CLOSE, dropSourceWsBeforeBlockClose)
        .replace(HTML_BLOCK_BOUNDARY, '\n'),
    ),
  )
  const lines = text.split('\n')
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop()
  return collapseBlankLineRuns(lines).join('\n')
}

export const obituaryPlainTextToHtml = (plain: string): string => {
  const esc = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const lines = String(plain ?? '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
  return collapseBlankLineRuns(lines)
    .map((line) => `<p>${esc(line)}</p>`)
    .join('')
}

export const SERVICE_BLOCK_DATE_FORMAT = 'dddd, MMMM D, YYYY'

type CaseServiceRow = Record<string, unknown>

const str = (v: unknown): string => (typeof v === 'string' ? v : '')

const cityStatePostal = (row: CaseServiceRow): string =>
  formatCityStatePostal({
    city: str(row.city),
    state: str(row.state),
    postal: str(row.postal_code),
  })

const addServiceKeys = (
  data: Record<string, string>,
  services: readonly CaseServiceRow[],
  ctx?: EditorTextBoxCaseContext,
): void => {
  for (const row of services) {
    const id = str(row.id)
    if (!id) continue
    data[serviceBlockPartKey(id, 'location')] = str(row.location)
    data[serviceBlockPartKey(id, 'address')] = str(row.address)
    data[serviceBlockPartKey(id, 'city_state_postal')] = cityStatePostal(row)
    data[serviceBlockPartKey(id, 'date')] =
      row.date == null || !ctx
        ? ''
        : ctx.formatDate(
            row.date as Date | string,
            SERVICE_BLOCK_DATE_FORMAT,
            true,
            ctx.locale,
          )
    data[serviceBlockPartKey(id, 'start_time')] = str(row.start_time)
    data[serviceBlockPartKey(id, 'end_time')] = str(row.end_time)
  }
  for (const kind of ['service', 'visitation'] as const) {
    const def = services.find(
      (s) => Boolean(s.default) && str(s.type).toLowerCase() === kind,
    )
    if (def) data[serviceDefaultIdKey(kind)] = str(def.id)
  }
}

/** The columns of each service row the case data publishes for a `service_column` run. */
export const SERVICE_SCALAR_COLUMNS = [
  'location',
  'time',
  'date',
  'city',
  'state',
  'start_time',
  'end_time',
  'clergy_place',
  'room_chapel',
  'address',
  'postal_code',
  'country',
  'type',
] as const

const addServiceColumnKeys = (
  data: Record<string, string>,
  services: readonly CaseServiceRow[],
  ctx?: EditorTextBoxCaseContext,
): void => {
  services.forEach((row, index) => {
    const id = str(row.id)
    if (!id) return
    data[editorServiceIndexKey(index)] = id
    for (const column of SERVICE_SCALAR_COLUMNS)
      data[editorServiceColumnKey(id, column)] =
        column === 'date' ? asDate(row.date, ctx) : str(row[column])
  })
}

const FH_MAX_LEN = 500

const FH_FIELD_RULES: readonly { key: string; from: string }[] =
  FH_FIELD_SOURCES

const cap = (v: string): string => v.slice(0, FH_MAX_LEN)

const addFuneralHomeKeys = (
  data: Record<string, string>,
  fh: Record<string, unknown>,
): void => {
  for (const rule of FH_FIELD_RULES)
    data[rule.key] = cap(trimWrittenCaseValue(str(fh[rule.from])))
  data['fh:city_state_zip'] = cap(
    formatCityStatePostal({
      city: str(fh.city),
      state: str(fh.state),
      postal: str(fh.zip_code),
    }),
  )
}

const HEADER_FIELD_KEYS: readonly string[] = EDITOR_FIELD_CATALOG.filter(
  (e) => e.group === 'Headers',
).map((e) => e.key)

const TABULAR_FIELD_KEYS: readonly string[] = EDITOR_FIELD_CATALOG.filter(
  (e) => e.kind === 'tabular',
).map((e) => e.key)

const cellString = (v: unknown): string => (typeof v === 'string' ? v : '')

type PallbearerRow = { name: string; type?: string; sequence?: unknown }

const isNamedRow = (item: unknown): item is PallbearerRow =>
  typeof item === 'object' &&
  item !== null &&
  typeof (item as Record<string, unknown>).name === 'string'

const pallbearerNames = (items: unknown, honorary: boolean): string[] => {
  if (!Array.isArray(items)) return []
  if (items.every((item) => typeof item === 'string'))
    return honorary ? [] : (items as string[])
  return orderedBySequence(items.filter(isNamedRow))
    .filter((row) => isHonoraryPallbearerRow(row) === honorary)
    .map((row) => row.name)
}

const toCamelCase = (snakeCaseName: string): string =>
  snakeCaseName.replace(/_([a-z])/g, (_, letter: string) =>
    letter.toUpperCase(),
  )

const readEitherSpelling = (
  record: Record<string, unknown>,
  snakeCaseName: string,
): string =>
  cellString(record[snakeCaseName]) ||
  cellString(record[toCamelCase(snakeCaseName)])

const joinRowFields = (row: unknown, fields: readonly string[]): string => {
  if (typeof row !== 'object' || row === null) return ''
  const record = row as Record<string, unknown>
  return fields
    .map((name) => readEitherSpelling(record, name).trim())
    .filter((value) => value !== '')
    .join(', ')
}

const ORGANIZATION_FIELDS = [
  'name',
  'sequence',
  'address1',
  'address2',
  'city',
  'state',
  'zip_code',
  'country',
  'website_url',
] as const

const ADDITIONAL_FAMILY_KEY = 'additional_family'

const ADDITIONAL_FAMILY_FIELDS = [
  'name',
  'relation',
  'city',
  'state',
  'country',
] as const

type FamilyRow = Record<string, unknown>

const isFamilyRow = (item: unknown): item is FamilyRow =>
  typeof item === 'object' && item !== null && !Array.isArray(item)

const familyRows = (c: Record<string, unknown>): FamilyRow[] =>
  Array.isArray(c.additional_family)
    ? c.additional_family.filter(isFamilyRow)
    : []

/** One line per additional-family row with anything in it; naming a group, that group's rows only. */
const additionalFamilyLines = (
  c: Record<string, unknown>,
  rowType?: string,
): string[] =>
  familyRows(c)
    .filter(
      (row) =>
        rowType === undefined || additionalFamilyRowType(row) === rowType,
    )
    .map((row) => joinRowFields(row, ADDITIONAL_FAMILY_FIELDS))
    .filter((line) => line !== '')

const tabularCellValues = (
  key: string,
  c: Record<string, unknown>,
  rowType?: string,
): string[] => {
  if (key === 'pallbearers') return pallbearerNames(c.pallbearers, false)
  if (key === 'honorary_pallbearers')
    return [
      ...pallbearerNames(c.pallbearers, true),
      ...(Array.isArray(c.honorary_pallbearers)
        ? c.honorary_pallbearers.map(cellString)
        : []),
    ]
  if (key === 'memorial_contributions')
    return Array.isArray(c.memorial_contributions)
      ? c.memorial_contributions
          .map((row) => joinRowFields(row, ['name']))
          .filter((line) => line !== '')
      : []
  if (key === 'organizations')
    return Array.isArray(c.organizations)
      ? c.organizations
          .map((row) => joinRowFields(row, ORGANIZATION_FIELDS))
          .filter((line) => line !== '')
      : []
  if (key === ADDITIONAL_FAMILY_KEY) return additionalFamilyLines(c, rowType)
  const value = c[key]
  return Array.isArray(value) ? value.map(cellString) : []
}

/**
 * The cells a tabular field prints from a case, one string per row, as the
 * box renders them; an additional-family table naming a group prints that
 * group's.
 */
export const tabularCaseCells = (
  fieldKey: string,
  caseDetails: Record<string, unknown> | null | undefined,
  rowType?: string,
): string[] => tabularCellValues(fieldKey, caseDetails ?? {}, rowType)

const addTableCells = (
  data: Record<string, string>,
  fieldKey: string,
  values: readonly string[],
  rowType?: string,
): void => {
  data[editorTableCellCountKey(fieldKey, rowType)] = String(values.length)
  values.forEach((value, i) => {
    data[editorTableCellKey(fieldKey, i, rowType)] = value
  })
}

/**
 * Every table's cells over its whole column; then the additional-family
 * groups of the case, in the order the menu lists them, each with the cells
 * of its own rows.
 */
const addTableCellKeys = (
  data: Record<string, string>,
  c: Record<string, unknown>,
): void => {
  for (const key of TABULAR_FIELD_KEYS)
    addTableCells(data, key, tabularCellValues(key, c))
  additionalFamilyRowTypes(familyRows(c)).forEach((rowType, n) => {
    data[editorTableRowTypeIndexKey(ADDITIONAL_FAMILY_KEY, n)] = rowType
    addTableCells(
      data,
      ADDITIONAL_FAMILY_KEY,
      additionalFamilyLines(c, rowType),
      rowType,
    )
  })
}

const PRINTED_IN_I18N_KEY = 'editor.tinyMCE.insertDetail.printedInByMessenger'

const printedInLine = (ctx?: EditorTextBoxCaseContext): string => {
  if (ctx?.themeApplication !== M3) return ''
  const fh = (ctx.funeralHomeDetails ?? {}) as Record<string, unknown>
  let country = str(fh.ip_country_name) || 'U.S.A.'
  if (country.trim().toLowerCase() === 'united states') country = 'U.S.A.'
  return (
    ctx.translate?.(PRINTED_IN_I18N_KEY, { country }) ||
    `Printed in ${country} by Messenger`
  )
}

const FORMATTERS: Readonly<
  Record<string, (raw: unknown, ctx?: EditorTextBoxCaseContext) => string>
> = {
  date_of_birth: asDate,
  date_of_death: asDate,
  date_of_marriage: asDate,
  interment_date: asDate,
  year_of_birth: asYear,
  year_of_death: asYear,
  first_letter_first: asFirstLetter,
  first_letter_middle: asFirstLetter,
  first_letter_last: asFirstLetter,
  age_years: asAge('age_years'),
  age_months: asAge('age_months'),
  age_days: asAge('age_days'),
  obituary: (raw) => obituaryToPlainText(raw),
  order_of_service: (raw) => obituaryToPlainText(raw),
}

export const FIELD_RULES: readonly FieldRule[] = FIELD_SOURCES.map(
  (s): FieldRule => {
    if (!s.formatted) return { key: s.key, from: s.from }
    const format = FORMATTERS[s.key]
    if (!format) {
      console.error(
        `FIELD_SOURCES marks "${s.key}" formatted but no FORMATTER exists (single-source drift) — resolving the raw column value until one is added`,
      )
      return { key: s.key, from: s.from }
    }
    return { key: s.key, from: s.from, format }
  },
)

const WRITE_FORMATTERS: Readonly<
  Record<string, ((plain: string) => string) | undefined>
> = {
  obituary: obituaryPlainTextToHtml,
}

export type WritableField = {
  column: string
  transform?: (value: string) => string
}

export const fieldKeyToWritableCaseField = (
  fieldKey: string,
): WritableField | null => {
  const rule = FIELD_RULES.find((r) => r.key === fieldKey)
  if (!rule) return null
  if (!rule.format) return { column: rule.from }
  const transform = WRITE_FORMATTERS[fieldKey]
  return transform ? { column: rule.from, transform } : null
}

const DATE_PICKER_FIELDS: ReadonlySet<string> = new Set([
  'date_of_birth',
  'date_of_death',
  'date_of_marriage',
  'interment_date',
])

export const fieldKeyToDateColumn = (fieldKey: string): string | null => {
  if (!DATE_PICKER_FIELDS.has(fieldKey)) return null
  const rule = FIELD_RULES.find((r) => r.key === fieldKey)
  return rule ? rule.from : null
}

export function toEditorTextBoxCaseData(
  caseDetails: Record<string, unknown> | null | undefined,
  ctx?: EditorTextBoxCaseContext,
): EditorCaseData {
  const c = caseDetails ?? {}
  const data: Record<string, string> = {}
  for (const rule of FIELD_RULES) {
    const raw = c[rule.from]
    data[rule.key] = rule.format
      ? rule.format(raw, ctx, c)
      : typeof raw === 'string'
        ? trimWrittenCaseValue(raw)
        : ''
  }
  data['interment_city_state_postal'] = formatCityStatePostal({
    city: str(c.interment_city),
    state: str(c.interment_state),
    postal: str(c.interment_postal_code),
  })
  const prefs = (c.case_preferences ?? {}) as Record<string, unknown>
  for (const key of HEADER_FIELD_KEYS)
    data[key] = trimWrittenCaseValue(str(prefs[key]))
  addTableCellKeys(data, c)
  data['printed_in'] = printedInLine(ctx)
  if (Array.isArray(c.services)) {
    addServiceKeys(data, c.services as CaseServiceRow[], ctx)
    addServiceColumnKeys(data, c.services as CaseServiceRow[], ctx)
  }
  if (ctx?.funeralHomeDetails)
    addFuneralHomeKeys(data, ctx.funeralHomeDetails as Record<string, unknown>)
  data[EDITOR_FIELD_NAME_OF_DECEASED] = trimWrittenCaseValue(
    str(c.name_of_deceased),
  )
  const arrangement = nameArrangementSchema.safeParse(c.name_arrangement)
  if (arrangement.success)
    Object.assign(data, editorNameArrangementCaseData(arrangement.data))
  return data
}
